import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import TypeTravoPage from "./type_travo";
import AddWorkPage from "./ajoute_travaux";

import { getServices } from "../API/servicesApi";
import { getCategories } from "../API/categoriesApi";

const BASE_URL = "https://api.allianceprotravaux.be/api";

export default function ServicesPage({ search }) {
  const [categories, setCategories] = useState([]);
  const [works, setWorks] = useState([]);

  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingServices, setLoadingServices] = useState(true);

  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedWorkForTypes, setSelectedWorkForTypes] = useState(null);

  const [showAddWorkPanel, setShowAddWorkPanel] = useState(false);
  const [workToEdit, setWorkToEdit] = useState(null);

  const [activeWork, setActiveWork] = useState(null);
  const [newType, setNewType] = useState({ name: "", description: "", image: "" });

  /* ---------------- LOAD CATEGORIES ---------------- */

  useEffect(() => {
    async function loadCategories() {
      try {
        const data = await getCategories();
        setCategories(data);

        if (data.length > 0) {
          setActiveCategory(data[0].id);
        }

      } catch (err) {
        console.error("Categories error:", err);
      } finally {
        setLoadingCategories(false);
      }
    }

    loadCategories();
  }, []);

  /* ---------------- LOAD SERVICES ---------------- */

  useEffect(() => {
    async function loadServices() {
      try {

        setLoadingServices(true);

        const response = await getServices();
        const services = response.data;

        const formatted = services.map(s => ({
          id: s.id,
          category_id: s.category_id,
          name: s.title,
          description: s.short_description,
          fullDescription: s.full_description,   
          image: s.image,
          isActive: s.is_active,
          types: s.types || []
        }));

        setWorks(formatted);

      } catch (err) {
        console.error("Services error:", err);
      } finally {
        setLoadingServices(false);
      }
    }

    loadServices();
  }, []);

  /* ---------------- ADD SERVICE ---------------- */

  const addWork = async (formData) => {

    try {

      const res = await axios.post(`${BASE_URL}/services`, formData);
      const w = res.data;

      const newWork = {
        id: w.id,
        category_id: w.category_id,
        name: w.title,
        description: w.short_description,
        fullDescription: w.full_description,   
        image: w.image,
        isActive: w.is_active,
        types: w.types || []
      };

      setWorks(prev => [...prev, newWork]);

      // switch category so the new service appears immediately
      setActiveCategory(w.category_id);

      setShowAddWorkPanel(false);

    } catch (error) {

      console.log("Full error:", error.response);
      alert(JSON.stringify(error.response?.data?.errors));

    }
  };

  /* ---------------- UPDATE SERVICE ---------------- */

  const updateWork = async (formData, id) => {

    try {

      const res = await axios.patch(`${BASE_URL}/services/${id}`, formData);
      const w = res.data;

      setWorks(prev =>
        prev.map(work =>
          work.id === id
            ? {
                id: w.id,
                category_id: w.category_id,
                name: w.title,
                description: w.short_description,
                fullDescription: w.full_description,   // ← add this
                image: w.image ? `${w.image}?t=${Date.now()}` : "",
                isActive: w.is_active,
                types: w.types || []
              }
            : work
        )
      );

      setShowAddWorkPanel(false);

    } catch (err) {

      console.error("Erreur update:", err);

    }
  };

  /* ---------------- DELETE SERVICE ---------------- */

  const deleteWork = async (id) => {

    if (!window.confirm("Supprimer ce travail ?")) return;

    try {

      await axios.delete(`${BASE_URL}/services/${id}`);

      setWorks(prev => prev.filter(w => w.id !== id));

    } catch (err) {

      console.error("Erreur delete:", err);

    }
  };

  /* ---------------- TOGGLE STATUS ---------------- */

  const toggleWorkStatus = async (work) => {

    const updatedStatus = !work.isActive;

    try {

      await axios.patch(`${BASE_URL}/services/${work.id}`, {
        is_active: updatedStatus ? 1 : 0
      });

      setWorks(prev =>
        prev.map(w =>
          w.id === work.id
            ? { ...w, isActive: updatedStatus }
            : w
        )
      );

    } catch (err) {

      console.error("Erreur toggle status:", err);

    }
  };

  /* ---------------- ADD TYPE ---------------- */

  const addType = () => {

    if (!newType.name || !activeWork) return;

    setWorks(prev =>
      prev.map(work =>
        work.id === activeWork.id
          ? { ...work, types: [...work.types, { id: Date.now(), ...newType }] }
          : work
      )
    );

    setNewType({ name: "", description: "", image: "" });
  };

  /* ---------------- FILTER WORKS ---------------- */

  const filteredWorks = useMemo(() => {

    return works
      .filter(w => Number(w.category_id) === Number(activeCategory))
      .filter(w =>
        !search ||
        w.name?.toLowerCase().includes(search.toLowerCase())
      );

  }, [works, activeCategory, search]);

  /* ---------------- LOADING ---------------- */

  if (loadingCategories || loadingServices) {
    return <div style={{ padding: 40 }}>Loading services...</div>;
  }

  /* ---------------- UI ---------------- */

  return (
    <div className="services-page">

      {/* CATEGORY BAR */}

      <div className="category-bar">

        {categories.map(cat => (

          <div
            key={cat.id}
            className={`category-item ${activeCategory === cat.id ? "active" : ""}`}
            onClick={() => setActiveCategory(cat.id)}
          >
            {cat.name}
          </div>

        ))}

      </div>


      {/* HEADER */}

      <div className="works-section">

        <div className="works-header">

          <h2>Les travaux</h2>

          <button
            className="add-work-btn"
            onClick={() => {
              setWorkToEdit(null);
              setShowAddWorkPanel(true);
            }}
          >
            + Ajouter un travail
          </button>

        </div>

      </div>


      {/* TABLE */}

      <div className="works-table-wrapper">

        <div className="works-table-header">
          Liste des Travaux
        </div>

        <table className="works-table">

          <thead>
            <tr>
              <th>Image</th>
              <th>Nom</th>
              <th>Court Description</th>
              <th>Long Description</th>
              <th>Statut</th>
              <th>Types</th>
              <th>Actions</th>
              
            </tr>
          </thead>

          <tbody>

            {filteredWorks.map(work => (

              <tr key={work.id}>

                <td>
                  <img loading="lazy" src={work.image} alt="" className="table-img" />
                </td>

                <td>{work.name}</td>

                <td style={{
                  maxWidth: 300,
                  wordWrap: "break-word",
                  lineHeight: 1.4
                }}>
                  {work.description}
                </td>
                <td style={{ maxWidth: 300, wordWrap: "break-word", lineHeight: 1.4 }}>
                  {work.fullDescription}
                </td>

                <td>

                  <span
                    className={`badge ${work.isActive ? "active" : "inactive"}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleWorkStatus(work)}
                  >
                    {work.isActive ? "Actif" : "Inactif"}
                  </span>

                </td>

                <td>
                  {work.types?.length || 0} type(s)
                </td>

                <td>

                  <div className="table-actions">

                    <button
                      className="type-btn"
                      onClick={() => setSelectedWorkForTypes(work)}
                    >
                      Types
                    </button>

                    <button
                      className="edit-btn"
                      onClick={() => {
                        setWorkToEdit(work);
                        setShowAddWorkPanel(true);
                      }}
                    >
                      Edit
                    </button>

                    <button
                      className="delete-btn"
                      onClick={() => deleteWork(work.id)}
                    >
                      Delete
                    </button>

                  </div>

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>


      {/* TYPES PANEL */}

      {selectedWorkForTypes && (

        <TypeTravoPage
          work={selectedWorkForTypes}
          allWorks={works.filter(w => w.category_id === activeCategory)}
          onClose={() => setSelectedWorkForTypes(null)}
          onUpdate={updateWork}
        />

      )}


      {/* ADD / EDIT PANEL */}

      {showAddWorkPanel && (

        <AddWorkPage
          categories={categories}
          work={workToEdit}
          activeCategory={activeCategory}
          onAddWork={addWork}
          onUpdate={updateWork}
          onClose={() => setShowAddWorkPanel(false)}
        />

      )}

    </div>
  );
}